/**
 * The things in a maker's way and then in their shop: a kitchen with a board across its
 * table, a hand saw and what it throws, a dog's bed; a wheelbarrow of concrete; and the
 * workshop inside — the bench under the windows, the pegboard, the birdhouse on the bench.
 *
 * Like every prop (`props.ts`), each is drawn about its own origin from where it is and
 * how far through its own motion it is, and none keeps state.
 */

import type { Ink, Pt } from "./ink.js";
import { noise } from "./ink.js";
import type { View } from "./film.js";
import { clamp, ease, mix, span } from "./timing.js";

// ---- the kitchen ---------------------------------------------------------------------

/** The kitchen's wall and floor: a window with curtains, a picture, the floorboards. */
export function kitchen(ink: Ink, view: View, floor: number): void {
  const p = ink.palette;
  ink.wash(
    [
      { x: view.left - 20, y: floor },
      { x: view.right + 20, y: floor },
      { x: view.right + 20, y: view.bottom + 20 },
      { x: view.left - 20, y: view.bottom + 20 },
    ],
    p.wood,
    0.45,
    0,
  );
  ink.line(view.left - 20, floor, view.right + 20, floor, { width: 2.6 });
  ink.line(view.left - 20, floor - 12, view.right + 20, floor - 12, { width: 1.6, alpha: 0.6 });
  // The window over the dog's bed, curtains drawn back.
  const wx = 190;
  const wy = -70;
  ink.rect(wx, wy, 150, 120, { fill: p.sky, width: 3 });
  ink.line(wx + 75, wy, wx + 75, wy + 120, { width: 3, color: p.trim, overshoot: false });
  ink.line(wx, wy + 60, wx + 150, wy + 60, { width: 3, color: p.trim, overshoot: false });
  ink.rect(wx - 10, wy + 120, 170, 9, { fill: p.trim, width: 2.2, offset: 0 });
  for (const [side, x] of [
    [-1, wx - 4],
    [1, wx + 154],
  ] as const) {
    ink.shape(
      [
        { x, y: wy - 14 },
        { x: x + side * 26, y: wy - 14 },
        { x: x + side * 18, y: wy + 70 },
        { x: x + side * 30, y: wy + 132 },
        { x, y: wy + 132 },
      ],
      { fill: p.signal, fillAlpha: 0.55, width: 2.2 },
    );
  }
  ink.line(wx - 40, wy - 16, wx + 190, wy - 16, { width: 3.4, color: p.woodDark, overshoot: false });
}

/** A kitchen table: the top at `top`, legs down to the floor. */
export function table(ink: Ink, x0: number, x1: number, top: number, floor: number): void {
  const p = ink.palette;
  for (const lx of [x0 + 14, x1 - 22]) {
    ink.rect(lx, top + 10, 8, floor - top - 10, { fill: p.woodDark, width: 2.2, offset: 0.6 });
  }
  ink.rect(x0, top, x1 - x0, 12, { fill: p.woodDark, width: 2.6 });
}

/** A bowl of fruit, hopping when the table is knocked (`hop` 0–1). */
export function fruitBowl(ink: Ink, x: number, y: number, hop: number): void {
  const p = ink.palette;
  const lift = -Math.sin(clamp(hop) * Math.PI) * 10;
  for (const [dx, color] of [
    [-12, p.signal],
    [2, p.accent],
    [14, p.grass],
  ] as const) {
    ink.circle(x + dx, y - 14 + lift * 1.4, 9, { fill: color, width: 2, offset: 0 });
  }
  ink.shape(
    [
      { x: x - 30, y: y - 12 + lift },
      { x: x + 30, y: y - 12 + lift },
      { x: x + 18, y: y + lift },
      { x: x - 18, y: y + lift },
    ],
    { fill: p.trim, width: 2.4, offset: 0 },
  );
}

/** A dog's bed: a cushion on the floor. */
export function dogBed(ink: Ink, x: number, floor: number): void {
  const p = ink.palette;
  ink.ellipse(x, floor - 8, 58, 14, { fill: p.signal, fillAlpha: 0.7, width: 2.6, offset: 0 });
  ink.ellipse(x, floor - 13, 44, 8, { fill: p.paper, width: 1.6, offset: 0 });
}

// ---- tools -----------------------------------------------------------------------------

/**
 * A hand saw, its handle in `hand` and its blade along `angle` (radians, from +x): the
 * teeth on the lower edge.
 */
export function handSaw(ink: Ink, hand: Pt, angle: number, length = 74): void {
  const p = ink.palette;
  const ax = Math.cos(angle);
  const ay = Math.sin(angle);
  const nx = -ay;
  const ny = ax;
  const at = (along: number, across: number): Pt => ({ x: hand.x + ax * along + nx * across, y: hand.y + ay * along + ny * across });
  ink.shape([at(6, -9), at(length, -4), at(length, 3), at(6, 9)], { fill: p.trim, width: 2, overshoot: false, offset: 0 });
  for (let along = 12; along < length - 2; along += 6) {
    const a = at(along, 9 - (along / length) * 5);
    const b = at(along + 3, 12 - (along / length) * 5);
    ink.line(a.x, a.y, b.x, b.y, { width: 1.2, overshoot: false });
  }
  ink.shape([at(-8, -8), at(10, -10), at(12, 10), at(-6, 9)], { fill: p.woodDark, width: 2, overshoot: false, offset: 0 });
  ink.ellipse(at(2, 0).x, at(2, 0).y, 3.4, 5, { fill: p.paper, width: 1.2, offset: 0 });
}

/** A wrench, gripped at the end of its handle: for `Figure.hold`. */
export function wrenchInHand(ink: Ink, hand: Pt, angle: number): void {
  const p = ink.palette;
  const ax = Math.cos(angle);
  const ay = Math.sin(angle);
  const end = { x: hand.x + ax * 22, y: hand.y + ay * 22 };
  ink.line(hand.x - ax * 3, hand.y - ay * 3, end.x, end.y, { width: 4.4, color: p.roof, overshoot: false });
  ink.circle(end.x, end.y, 5.5, { fill: p.roof, width: 2, offset: 0 });
  ink.circle(end.x + ax * 2, end.y + ay * 2, 2.4, { fill: p.paper, width: 1, offset: 0 });
}

/** A screed board: a straight two-by held in both hands, struck across wet concrete. */
export function screed(ink: Ink, a: Pt, b: Pt): void {
  const p = ink.palette;
  // A ridge of concrete pushed along in front of the board, then the board on its edge.
  ink.shape(
    [
      { x: a.x + 4, y: a.y + 1 },
      { x: b.x - 4, y: b.y + 1 },
      { x: b.x - 10, y: b.y - 5 },
      { x: a.x + 10, y: a.y - 5 },
    ],
    { fill: p.roof, fillAlpha: 0.7, width: 1.4, overshoot: false, offset: 0 },
  );
  ink.shape(
    [
      { x: a.x, y: a.y },
      { x: b.x, y: b.y },
      { x: b.x, y: b.y + 9 },
      { x: a.x, y: a.y + 9 },
    ],
    { fill: p.wood, width: 2, overshoot: false, offset: 0.6 },
  );
}

/**
 * A wheelbarrow, its wheel at (`x`, `floor`), its handles lifted by `tip` (0–1) to pour
 * over the wheel; full of concrete while `full` is above zero. Returns where the handles
 * are gripped.
 */
export function wheelbarrow(ink: Ink, x: number, floor: number, tip: number, full: number): Pt {
  const p = ink.palette;
  const { ctx } = ink;
  const wheel = { x, y: floor - 14 };
  ctx.save();
  ctx.translate(wheel.x, wheel.y);
  ctx.rotate(-0.12 + tip * 0.7);
  // The tray, the load, the legs and the handles: all hinge about the wheel.
  ink.shape(
    [
      { x: -96, y: -46 },
      { x: -4, y: -46 },
      { x: 6, y: -18 },
      { x: -78, y: -14 },
    ],
    { fill: p.siding, width: 2.6, offset: 0.6 },
  );
  if (full > 0) {
    ink.shape(
      [
        { x: -92, y: -46 },
        { x: -60, y: -46 - 14 * full },
        { x: -30, y: -48 - 10 * full },
        { x: -8, y: -46 },
      ],
      { fill: p.roof, fillAlpha: 0.6, width: 2, offset: 0 },
    );
  }
  ink.line(-72, -16, -84, 12, { width: 3.4, overshoot: false });
  ink.line(-90, -40, -150, -26, { width: 4, color: p.woodDark, overshoot: false });
  ctx.restore();
  ink.circle(wheel.x, wheel.y, 14, { fill: p.ink, fillAlpha: 0.9, width: 2.4, offset: 0 });
  const a = -0.12 + tip * 0.7;
  return { x: wheel.x + Math.cos(a) * -150 - Math.sin(a) * -26, y: wheel.y + Math.sin(a) * -150 + Math.cos(a) * -26 };
}

/** Concrete sliding off the lip of a tipped barrow onto the slab, `k` 0–1 through the pour. */
export function pourStream(ink: Ink, from: Pt, to: Pt, k: number): void {
  if (k <= 0 || k >= 1) {
    return;
  }
  const p = ink.palette;
  const wobble = Math.sin(k * 40) * 3;
  ink.shape(
    [
      { x: from.x - 6, y: from.y },
      { x: from.x + 8, y: from.y },
      { x: to.x + 14 + wobble, y: to.y },
      { x: to.x - 12 + wobble, y: to.y },
    ],
    { fill: p.roof, fillAlpha: 0.55, width: 1.8, overshoot: false, offset: 0 },
  );
}

// ---- sawdust -------------------------------------------------------------------------

/**
 * Sawdust off a cut at (`x`, `y`) while the saw runs from `start` to `stop`: specks falling
 * to the floor and a little heap there that grows as they land.
 */
export function sawdust(ink: Ink, x: number, y: number, floor: number, u: number, start: number, stop: number): void {
  const p = ink.palette;
  const fall = 0.42;
  const every = 0.035;
  const count = Math.floor((stop - start) / every);
  let landed = 0;
  for (let i = 0; i < count; i += 1) {
    const born = start + i * every;
    const k = (u - born) / fall;
    if (k < 0) {
      continue;
    }
    if (k >= 1) {
      landed += 1;
      continue;
    }
    const drift = noise(i, 7) * 26;
    ink.dot(x + drift * k + noise(i, 3) * 4, mix(y, floor, k * k), 2 + Math.abs(noise(i, 5)) * 1.6, p.woodDark);
  }
  const heap = landed / Math.max(1, count);
  if (heap > 0) {
    ink.ellipse(x + 6, floor - 3 * heap, 10 + 28 * heap, 3 + 6 * heap, { fill: p.wood, width: 1.6, offset: 0 });
  }
}

/**
 * A billow of dust about (`x`, `y`): lumps of paper-white that swell to `radius` and thin
 * to nothing as `fade` goes to one. Anything under it disappears into it.
 */
export function dustCloud(ink: Ink, x: number, y: number, radius: number, fade: number): void {
  if (radius <= 0 || fade >= 1) {
    return;
  }
  const p = ink.palette;
  const lumps = 11;
  for (let i = 0; i < lumps; i += 1) {
    const a = (i / lumps) * Math.PI * 2 + noise(i, 11) * 0.4;
    const r = radius * (0.42 + 0.12 * noise(i, 13));
    const cx = x + Math.cos(a) * radius * 0.5;
    const cy = y + Math.sin(a) * radius * 0.36;
    ink.circle(cx, cy, r, { fill: p.paper, fillAlpha: 0.94 * (1 - fade), width: 2.2, alpha: 0.6 * (1 - fade), offset: 0 });
  }
  ink.circle(x, y, radius * 0.62, { fill: p.paper, fillAlpha: 0.94 * (1 - fade), stroke: false, offset: 0 });
}

/** Specks of sawdust settled on somebody: on the cap, the shoulders, the sleeves. */
export function dusted(ink: Ink, points: readonly Pt[], k: number): void {
  if (k <= 0) {
    return;
  }
  const p = ink.palette;
  points.forEach((point, j) => {
    for (let i = 0; i < 5; i += 1) {
      ink.dot(point.x + noise(i, j * 17) * 12, point.y + noise(i, j * 17 + 1) * 8, 2.2, p.wood, k);
    }
  });
}

// ---- the workshop inside ------------------------------------------------------------

/** A sturdy bench: a thick top, a vise at its end, a shelf under it. */
export function workbench(ink: Ink, x0: number, x1: number, top: number, floor: number): void {
  const p = ink.palette;
  for (const lx of [x0 + 10, x1 - 26]) {
    ink.rect(lx, top + 18, 16, floor - top - 18, { fill: p.woodDark, width: 2.4, offset: 0.6 });
  }
  ink.rect(x0 + 10, floor - 46, x1 - x0 - 20, 10, { fill: p.woodDark, width: 2, offset: 0.6 });
  ink.rect(x0, top, x1 - x0, 18, { fill: p.wood, width: 3 });
  ink.line(x0, top + 6, x1, top + 6, { width: 1.2, alpha: 0.4, overshoot: false });
  // The vise on the near end.
  ink.rect(x0 - 16, top + 2, 16, 26, { fill: p.roof, width: 2.2, offset: 0 });
  ink.line(x0 - 26, top + 16, x0 - 16, top + 16, { width: 3, overshoot: false });
}

/** A pegboard with the tools on it in their outlines: a saw, a hammer, a square, clamps. */
export function pegboard(ink: Ink, x: number, y: number, w: number, h: number): void {
  const p = ink.palette;
  ink.rect(x, y, w, h, { fill: p.wood, width: 2.6 });
  for (let px = x + 12; px < x + w - 6; px += 18) {
    for (let py = y + 12; py < y + h - 6; py += 18) {
      ink.dot(px, py, 1.4, p.woodDark, 0.8);
    }
  }
  handSaw(ink, { x: x + 22, y: y + 26 }, 0.2, 70);
  // A hammer, head up.
  ink.line(x + 120, y + 30, x + 120, y + 92, { width: 5, color: p.woodDark, overshoot: false });
  ink.rect(x + 106, y + 22, 28, 12, { fill: p.roof, width: 2, offset: 0 });
  // A framing square.
  ink.poly(
    [
      { x: x + 152, y: y + 20 },
      { x: x + 152, y: y + 100 },
      { x: x + 200, y: y + 100 },
    ],
    false,
    { width: 6, color: p.accent, overshoot: false },
  );
  // Two clamps.
  for (const cx of [x + 40, x + 70]) {
    ink.poly(
      [
        { x: cx, y: y + 62 },
        { x: cx, y: y + 104 },
        { x: cx + 14, y: y + 104 },
      ],
      false,
      { width: 3.4, color: p.signal, overshoot: false },
    );
    ink.line(cx + 14, y + 62, cx + 14, y + 80, { width: 3, color: p.roof, overshoot: false });
  }
}

/** A window in the workshop's back wall, and the light it lets in, falling to the floor. */
export function shopWindow(ink: Ink, x: number, y: number, w: number, h: number, floor: number, glow: number): void {
  const p = ink.palette;
  if (glow > 0) {
    ink.wash(
      [
        { x, y: y + h },
        { x: x + w, y: y + h },
        { x: x + w + 80, y: floor },
        { x: x + 60, y: floor },
      ],
      p.accent,
      0.16 * glow,
      0,
    );
  }
  ink.rect(x, y, w, h, { fill: p.glass, width: 3 });
  ink.line(x + w / 2, y, x + w / 2, y + h, { width: 3, color: p.trim, overshoot: false });
  ink.line(x, y + h / 2, x + w, y + h / 2, { width: 3, color: p.trim, overshoot: false });
  ink.rect(x - 8, y + h, w + 16, 7, { fill: p.trim, width: 2, offset: 0 });
}

/**
 * A birdhouse, the hole and the perch facing the viewer, its base at (`x`, `y`), turned by
 * `rotation`.
 */
export function birdhouse(ink: Ink, x: number, y: number, scale = 1, rotation = 0): void {
  const p = ink.palette;
  const { ctx } = ink;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.scale(scale, scale);
  ink.rect(-16, -34, 32, 34, { fill: p.wood, width: 2.4, offset: 0.4 });
  ink.shape(
    [
      { x: -22, y: -32 },
      { x: 0, y: -52 },
      { x: 22, y: -32 },
    ],
    { fill: p.accent, width: 2.4, offset: 0.4 },
  );
  ink.circle(0, -20, 6, { fill: p.ink, width: 1.6, offset: 0 });
  ink.line(0, -8, 0, -2, { width: 1.8, overshoot: false });
  ink.line(-5, -4, 5, -4, { width: 2.6, color: p.woodDark, overshoot: false });
  ctx.restore();
}

/** A dog's paw print, pressed into something soft. */
export function pawPrint(ink: Ink, x: number, y: number, size: number, alpha = 1): void {
  const p = ink.palette;
  ink.ellipse(x, y, 3.4 * size, 2.6 * size, { fill: p.ink, fillAlpha: 0.55 * alpha, stroke: false, offset: 0 });
  for (let i = 0; i < 4; i += 1) {
    const a = -Math.PI / 2 + (i - 1.5) * 0.55;
    ink.dot(x + Math.cos(a) * 4.6 * size, y + Math.sin(a) * 3.4 * size, 1.2 * size, p.ink, 0.55 * alpha);
  }
}

/** A mat by the door, for a dog to sleep on. */
export function mat(ink: Ink, x: number, floor: number, w = 110): void {
  const p = ink.palette;
  ink.ellipse(x, floor - 4, w / 2, 9, { fill: p.siding, fillAlpha: 0.75, width: 2.2, offset: 0 });
  ink.ellipse(x, floor - 5, w / 2 - 10, 5, { width: 1.2, alpha: 0.5, offset: 0 });
}

/** How far a motion is through a list of strokes, as a sawing hand moves: back and forth. */
export function stroke(u: number, start: number, stop: number, rate: number): number {
  const k = span(u, start, stop);
  if (k <= 0 || k >= 1) {
    return 0;
  }
  return Math.sin((u - start) * rate * Math.PI * 2) * ease.out(Math.min(1, (u - start) * 4));
}

/**
 * When each stroke of `stroke` begins — every half turn of it from `start` — whether it is
 * a push (the first, and every other one after), and how long it runs: for a sound on
 * every stroke the picture draws.
 */
export function strokeStarts(start: number, stop: number, rate: number): { at: number; push: boolean; length: number }[] {
  const half = 1 / (2 * rate);
  const strokes: { at: number; push: boolean; length: number }[] = [];
  for (let n = 0; start + n * half < stop; n += 1) {
    const at = start + n * half;
    strokes.push({ at, push: n % 2 === 0, length: Math.min(half, stop - at) });
  }
  return strokes;
}
